import type { RefObject } from 'react';
import React, { useEffect, useRef } from 'react';

import { ButtonBase } from '@mui/material';

import { ScrollbarController } from './controllers/ScrollbarController';
import { ScrollButtonController } from './controllers/ScrollButtonController';
import { useScrollbarStyles } from './hooks/use-scrollbar-styles';
import { ScrollDownIcon } from './icons/ScrollDownIcon';
import { ScrollLeftIcon } from './icons/ScrollLeftIcon';
import { ScrollRightIcon } from './icons/ScrollRightIcon';
import { ScrollUpIcon } from './icons/ScrollUpIcon';

export interface ScrollbarProps {
  direction: 'horizontal' | 'vertical';
  scrollableId: string;
  scrollableInnerRef: RefObject<HTMLDivElement | null>;
  scrollableRef: RefObject<HTMLDivElement | null>;
  /** @see UseScrollableOptions */
  visibleWhenNotOverflowing?: boolean;
}

export function Scrollbar({
  direction,
  scrollableId,
  scrollableInnerRef,
  scrollableRef,
  visibleWhenNotOverflowing = false,
}: ScrollbarProps) {
  const { classes } = useScrollbarStyles({ direction, visibleWhenNotOverflowing });
  const isHorizontal = direction === 'horizontal';

  const scrollbarRef = useRef<HTMLDivElement | null>(null);
  const scrollbarInnerRef = useRef<HTMLDivElement | null>(null);
  const scrollbarThumbRef = useRef<HTMLDivElement | null>(null);
  const scrollUpButtonRef = useRef<HTMLButtonElement | null>(null);
  const scrollDownButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const { current: scrollable } = scrollableRef;
    const { current: scrollableInner } = scrollableInnerRef;
    const { current: scrollbar } = scrollbarRef;
    const { current: scrollbarInner } = scrollbarInnerRef;
    const { current: scrollbarThumb } = scrollbarThumbRef;
    const { current: scrollUpButton } = scrollUpButtonRef;
    const { current: scrollDownButton } = scrollDownButtonRef;
    if (
      !scrollable ||
      !scrollableInner ||
      !scrollbar ||
      !scrollbarInner ||
      !scrollbarThumb ||
      !scrollUpButton ||
      !scrollDownButton
    )
      return;

    const scrollbarController = new ScrollbarController({
      direction,
      scrollable,
      scrollableInner,
      scrollbar,
      scrollbarInner,
      scrollbarThumb,
    });

    const scrollUpButtonController = new ScrollButtonController({
      button: scrollUpButton,
      direction,
      scrollable,
      sign: -1,
    });

    const scrollDownButtonController = new ScrollButtonController({
      button: scrollDownButton,
      direction,
      scrollable,
      sign: 1,
    });

    const disconnectScrollbar = scrollbarController.connect();
    const disconnectScrollUpButton = scrollUpButtonController.connect();
    const disconnectScrollDownButton = scrollDownButtonController.connect();

    return () => {
      disconnectScrollbar();
      disconnectScrollUpButton();
      disconnectScrollDownButton();
    };
  }, [direction, scrollableInnerRef, scrollableRef]);

  return (
    <div ref={scrollbarRef} className={classes.scrollbar}>
      <ButtonBase
        ref={scrollUpButtonRef}
        aria-controls={scrollableId}
        aria-label={`Scroll ${isHorizontal ? 'left' : 'up'}`}
        className={classes.scrollButton}
      >
        {isHorizontal ? <ScrollLeftIcon /> : <ScrollUpIcon />}
      </ButtonBase>

      <div
        ref={scrollbarInnerRef}
        aria-controls={scrollableId}
        aria-label="Scrollbar"
        aria-orientation={direction}
        className={classes.scrollbarInner}
        role="scrollbar"
      >
        <div ref={scrollbarThumbRef} className={classes.scrollbarThumb}>
          <div className={classes.scrollbarThumbVisible} />
        </div>
      </div>

      <ButtonBase
        ref={scrollDownButtonRef}
        aria-controls={scrollableId}
        aria-label={`Scroll ${isHorizontal ? 'right' : 'down'}`}
        className={classes.scrollButton}
      >
        {isHorizontal ? <ScrollRightIcon /> : <ScrollDownIcon />}
      </ButtonBase>
    </div>
  );
}
